import { FC, useMemo } from "react";
import { isValidSubMenu } from "@utils/app.util";
import { Button } from "@components/button";
import { CustomTooltip } from "@components/tool-tip";
import { DownArrow, RightIcon, UpArraow } from "@components/icons";
import { AppMenuSchemaType } from "@dialogs/app-add-edit-dialog/app-add-edit-form.schema";
import classNames from "classnames";
import { useAppLive } from "@contexts/live-app.context";

interface MenuProps {
  menuData: AppMenuSchemaType[];
  appId?: string;
  className?: string;
  fontColor?: string;
  bgColor?: string;
  toggleLeftMenu?: React.Dispatch<React.SetStateAction<boolean>>;
  parentMenuPath?: string;
}

export const Menu: FC<MenuProps> = ({
  menuData,
  appId,
  className,
  fontColor,
  bgColor,
  toggleLeftMenu,
  parentMenuPath,
}) => {
  const { state, isActiveRoute, onMenuClick, isAncestorMenu } = useAppLive();
  const menuStyle = useMemo(
    () => ({
      color: fontColor,
      "--menu-bg-color": bgColor,
    }),
    [fontColor, bgColor],
  );
  return (
    <ul className="top-menu">
      {menuData.map((menu) => {
        // unique hierarchical path
        const currentPath = parentMenuPath ? `${parentMenuPath}/${menu._id}` : `${menu._id}`;
        const isSubMenu = isValidSubMenu(menu);
        const isActive = !isSubMenu && isActiveRoute(menu._id as string);
        const isOpen = !!state.expanded[currentPath as string];
        const isSubMenuHighlighted = isSubMenu && (isOpen || isAncestorMenu(menu._id as string));
        return (
          <li
            key={menu._id}
            className={classNames(className, {
              parentMenu: isSubMenu,
              showMenu: !!parentMenuPath && isOpen,
            })}
          >
            <Button
              style={menuStyle}
              className={classNames("menu-btn", {
                "active-menu": isActive,
                "submenu-bg": isSubMenuHighlighted,
              })}
              variant="transparent"
              onClick={() => {
                onMenuClick({
                  menu: menu,
                  appId: appId as string,
                  parentMenuPath: currentPath,
                });
              }}
            >
              {menu.title.length > 20 ? (
                <CustomTooltip delay={2} direction="right" content={menu.title} zIndex={99999}>
                  {menu.title.slice(0, 20) + "..."}
                </CustomTooltip>
              ) : (
                menu.title
              )}

              {parentMenuPath && isSubMenu ? (
                state.expanded[currentPath] ? (
                  <UpArraow />
                ) : (
                  <DownArrow />
                )
              ) : isSubMenu ? (
                <RightIcon size="15" color={fontColor} />
              ) : (
                <></>
              )}
            </Button>

            {isSubMenu && state.expanded[currentPath] && (
              <Menu
                menuData={menu.children as AppMenuSchemaType[]}
                appId={appId}
                className="submenu"
                toggleLeftMenu={toggleLeftMenu}
                parentMenuPath={currentPath}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
};
